import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import Swal from "sweetalert2";
import MainLayout from "../../../components/layout/MainLayout";
import SanaNoteForm from "../SanaNote/SanaNoteForm";
import { createSanaNote } from "../../../store/slices/sanaNotesSlice";
import { fetchPatient } from "../../../store/slices/patientsSlice";

const AddSanaNote = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { state } = useLocation();
    const { caseId, patientId } = state || {};

    const [formData, setFormData] = useState({
        patientId, // initialize
        caseId,    // initialize
        clientName: "",
        progressNotes: [],
        documentName: "Progress Notes (Physician)",
        effectiveDate: "",
        approvedBy: "",
        approvedDate: "",
        nextRevisionBy: "",
        nextRevisionDate: "",
    });

    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (patientId) {
            dispatch(fetchPatient(patientId))
                .unwrap()
                .then((patient) => {
                    if (patient) {
                        setFormData((prev) => ({
                            ...prev,
                            clientName: `${patient.first_name || ""} ${patient.last_name || ""}`.trim(),
                        }));
                    }
                })
                .catch((err) => console.error("Failed to fetch patient details:", err));
        }
    }, [dispatch, patientId]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validation
        if (!formData.clientName) {
            setErrors({ clientName: "Client name is required." });
            Swal.fire({
                title: "Missing Fields",
                text: "Please fill in all required fields before submitting.",
                icon: "warning",
            });
            return;
        }

        // Check if at least one progress note row is filled
        const hasFilledRow = formData.progressNotes.some((row) => {
            return (
                row.dateTime ||
                row.pulse ||
                row.bp ||
                row.rr ||
                row.temp ||
                row.weight ||
                row.bsf ||
                row.bsr ||
                row.physicianNote ||
                row.nurseName ||
                row.height ||
                row.bmi ||
                row.abdCirc ||
                row.chestCirc ||
                row.hba1c ||
                row.urea ||
                row.crt ||
                row.lipidPro
            );
        });

        if (!hasFilledRow) {
            setErrors({ progressNotes: "Please fill at least one progress note row." });
            Swal.fire({
                title: "Missing Fields",
                text: "Please fill at least one progress note row before submitting.",
                icon: "warning",
            });
            return;
        }

        // Transform camelCase to snake_case
        const payload = {
            patient_id: formData.patientId || null,
            case_id: formData.caseId || null,
            client_name: formData.clientName,
            progress_notes: formData.progressNotes.map((note) => ({
                date_time: note.dateTime,
                pulse: note.pulse,
                bp: note.bp,
                rr: note.rr,
                temp: note.temp,
                weight: note.weight,
                bsf: note.bsf,
                bsr: note.bsr,
                height: note.height,
                bmi: note.bmi,
                abd_circ: note.abdCirc,
                chest_circ: note.chestCirc,
                hba1c: note.hba1c,
                urea: note.urea,
                crt: note.crt,
                lipid_pro: note.lipidPro,
                physician_note: note.physicianNote,
                nurse_name: note.nurseName,
            })),
            document_name: formData.documentName,
            effective_date: formData.effectiveDate,
            approved_by: formData.approvedBy,
            approved_date: formData.approvedDate,
            next_revision_by: formData.nextRevisionBy,
            next_revision_date: formData.nextRevisionDate,
        };

        try {
            await dispatch(createSanaNote(payload)).unwrap();

            Swal.fire({
                title: "Success!",
                text: "Progress notes added successfully.",
                icon: "success",
                timer: 2000,
                showConfirmButton: false,
            });

            navigate(`/cases/${payload.case_id}/profile`, { state: { patientId: payload.patient_id, caseId: payload.case_id } });
        } catch (error) {
            Swal.fire({
                title: "Error!",
                text: error?.message || "Failed to create Progress Note.",
                icon: "error",
            });
        }
    };

    return (
        <MainLayout>
            <div className="container-xxl flex-grow-1 container-p-y">
                <div className="card">
                    <div className="card-body">
                        {/* Validation Errors */}
                        {Object.keys(errors).length > 0 && (
                            <div className="alert alert-danger">
                                {Object.values(errors)
                                    .filter((err) => err)
                                    .map((err, i) => (
                                        <div key={i}>{err}</div>
                                    ))}
                            </div>
                        )}

                        <form onSubmit={handleSubmit}>
                            <SanaNoteForm
                                formData={formData}
                                setFormData={setFormData}
                                handleChange={handleChange}
                            />
                            {/* ==== SUBMIT ==== */}
                            <div className="col-12 d-flex justify-content-end mt-4">
                                <div className="col-md-2 d-flex justify-content-end">
                                    <button type="submit" className="btn btn-primary px-4">
                                        <i className="ri-save-2-line me-1"></i> Save
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </MainLayout>
    );
};

export default AddSanaNote;
